/**
 * UserQuestionCard - tappable options for the ask_user_question tool.
 *
 * The agent asks 1-4 structured questions, each with 2-4 options. Each option
 * is its own full-width row with a selection indicator (radio for
 * single-select, checkbox for multi-select), a label, and optionally a short
 * description. The user answers by tapping; a built-in "Something else"
 * reveals a free-text field per question. Submitting posts the answers back
 * to the backend tool call (via answerUserQuestion in hooks.js), which
 * unblocks the agent's turn.
 *
 * Styling mirrors the composer (chatStyles InputRowContainer): neutral
 * border, matching radius, warm accent only on hover/selection.
 *
 * One-tap fast path: a single single-select question submits immediately on
 * tap. Anything else shows an explicit send button.
 *
 * Double-submit guard: once submitted, the card locks (no re-picks, no
 * second POST). hooks.js also dismisses the card on submit; this guard is
 * defense in depth for the render frames in between.
 */

import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { getChatRadii, getChatBorders } from '../chatStyles';

const FREE_TEXT = '__free_text__';

// Options may arrive as plain strings or { label, description } objects.
const normalizeOption = (opt) =>
  typeof opt === 'string'
    ? { label: opt, description: '' }
    : { label: opt?.label || '', description: opt?.description || '' };

const UserQuestionCard = ({ request, onSubmit }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentWarm = theme.palette.accentBubble?.warm || '#D4694E';
  const radii = getChatRadii(theme);
  const borders = getChatBorders(theme);

  const questions = Array.isArray(request?.questions) ? request.questions : [];

  // Per-question answer state: { picked: string[], freeText, freeOpen }
  const [answers, setAnswers] = useState(() => questions.map(() => ({ picked: [], freeText: '', freeOpen: false })));
  const [submitted, setSubmitted] = useState(false);

  const singleTapMode = questions.length === 1 && !questions[0]?.multiSelect;

  const buildPayload = (answerState) =>
    questions.map((q, i) => {
      const entry = answerState[i] || { picked: [], freeText: '', freeOpen: false };
      const values = [...entry.picked];
      if (entry.freeOpen && entry.freeText.trim()) values.push(entry.freeText.trim());
      return {
        question: q.question,
        answer: q.multiSelect ? values : (values[0] || '')
      };
    });

  const isComplete = (answerState) =>
    questions.every((q, i) => {
      const entry = answerState[i] || { picked: [], freeText: '', freeOpen: false };
      return entry.picked.length > 0 || (entry.freeOpen && entry.freeText.trim().length > 0);
    });

  const submit = (payload) => {
    if (submitted) return;
    setSubmitted(true);
    onSubmit(payload);
  };

  const handlePick = (qIndex, label) => {
    if (submitted) return;
    const q = questions[qIndex];
    const next = answers.map((entry, i) => {
      if (i !== qIndex) return entry;
      if (label === FREE_TEXT) {
        const freeOpen = !entry.freeOpen;
        return { ...entry, freeOpen, picked: q.multiSelect ? entry.picked : (freeOpen ? [] : entry.picked) };
      }
      if (q.multiSelect) {
        const picked = entry.picked.includes(label)
          ? entry.picked.filter((p) => p !== label)
          : [...entry.picked, label];
        return { ...entry, picked };
      }
      return { ...entry, picked: [label], freeOpen: false, freeText: '' };
    });
    setAnswers(next);
    if (singleTapMode && label !== FREE_TEXT) {
      submit(buildPayload(next));
    }
  };

  const handleFreeText = (qIndex, value) => {
    if (submitted) return;
    setAnswers(answers.map((entry, i) => (i === qIndex ? { ...entry, freeText: value } : entry)));
  };

  // Full-width option row: indicator + label column. Neutral border at rest,
  // warm accent border + faint tint when selected/hovered.
  const optionRowSx = (selected) => ({
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 1,
    cursor: submitted ? 'default' : 'pointer',
    appearance: 'none',
    textTransform: 'none',
    px: 1.1,
    py: 0.75,
    // A row nested inside the question card.
    borderRadius: radii.nested,
    border: `${borders.width}px solid ${selected ? alpha(accentWarm, isDark ? 0.5 : 0.45) : borders.restingColor}`,
    backgroundColor: selected ? alpha(accentWarm, isDark ? 0.1 : 0.06) : 'transparent',
    color: theme.palette.text.primary,
    opacity: submitted && !selected ? 0.45 : 1,
    transition: 'border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease',
    '&:hover': submitted ? {} : {
      borderColor: alpha(accentWarm, isDark ? 0.45 : 0.4),
      backgroundColor: selected ? alpha(accentWarm, isDark ? 0.1 : 0.06) : alpha(accentWarm, isDark ? 0.05 : 0.035)
    }
  });

  // Selection indicator: circle for single-select (radio), rounded square
  // for multi-select (checkbox). Filled with the warm accent when selected.
  const indicator = (selected, multiSelect) => (
    <Box
      aria-hidden
      sx={{
        flexShrink: 0,
        width: 14,
        height: 14,
        mt: '2px',
        // Deliberate exception to the radius scale, shared with the markdown
        // task-list checkbox: at 14px the 8px chip radius rounds a checkbox
        // into a circle and erases the square vs round distinction that tells
        // multi-select apart from single-select. 4px is the one off-scale
        // radius in the chat pane. Both use the emphasis width, since the
        // indicator IS the selection affordance.
        borderRadius: multiSelect ? '4px' : radii.circle,
        border: `${borders.emphasisWidth}px solid ${selected ? accentWarm : borders.emphasisColor}`,
        backgroundColor: selected ? accentWarm : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'border-color 0.15s ease, background-color 0.15s ease'
      }}
    >
      {selected && (
        <Box
          component="span"
          sx={
            multiSelect
              ? { color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }
              : { width: 5, height: 5, borderRadius: radii.circle, backgroundColor: '#fff' }
          }
        >
          {multiSelect ? '✓' : null}
        </Box>
      )}
    </Box>
  );

  if (questions.length === 0) return null;

  return (
    <Box
      sx={{
        mx: 1,
        mb: 0.75,
        p: 1.25,
        borderRadius: radii.card,
        border: borders.resting,
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        // Compact + self-contained: long multi-question cards scroll inside
        // themselves instead of walling off the conversation.
        maxHeight: '46vh',
        overflowY: 'auto'
      }}
    >
      {questions.map((q, qIndex) => {
        const entry = answers[qIndex] || { picked: [], freeText: '', freeOpen: false };
        const options = (q.options || []).map(normalizeOption);
        return (
          <Box
            key={`${q.header}-${qIndex}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              // Visible seam between stacked questions so a multi-question
              // card reads as distinct blocks, not one long list.
              ...(qIndex > 0 && {
                pt: 1.25,
                borderTop: `${borders.width}px solid ${theme.palette.divider}`
              })
            }}
          >
            {/* Header chip on its own line, question text below it, so long
                questions never wrap awkwardly around the chip. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  px: 0.9,
                  py: 0.2,
                  borderRadius: radii.chip,
                  backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.07),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.pxToRem(10),
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap'
                }}
              >
                {q.header || `Q${qIndex + 1}`}
              </Box>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.pxToRem(13),
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  lineHeight: 1.4
                }}
              >
                {q.question}
              </Typography>
              {q.multiSelect && (
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.pxToRem(10.5),
                    color: theme.palette.text.secondary,
                    lineHeight: 1.3
                  }}
                >
                  Select all that apply
                </Typography>
              )}
            </Box>

            {/* One option per row: indicator + label (+ description). */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {options.map((option) => {
                const selected = entry.picked.includes(option.label);
                return (
                  <Box
                    key={option.label}
                    component="button"
                    type="button"
                    disabled={submitted}
                    onClick={() => handlePick(qIndex, option.label)}
                    sx={optionRowSx(selected)}
                  >
                    {indicator(selected, !!q.multiSelect)}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.pxToRem(12),
                          fontWeight: selected ? 600 : 500,
                          lineHeight: 1.35,
                          color: 'inherit'
                        }}
                      >
                        {option.label}
                      </Typography>
                      {option.description && (
                        <Typography
                          sx={{
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.pxToRem(10.5),
                            fontWeight: 400,
                            lineHeight: 1.4,
                            color: theme.palette.text.secondary
                          }}
                        >
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {/* Free-text escape hatch as its own row. */}
              <Box
                component="button"
                type="button"
                disabled={submitted}
                onClick={() => handlePick(qIndex, FREE_TEXT)}
                sx={optionRowSx(entry.freeOpen)}
              >
                {indicator(entry.freeOpen, false)}
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.pxToRem(12),
                    fontWeight: entry.freeOpen ? 600 : 500,
                    lineHeight: 1.35,
                    color: 'inherit'
                  }}
                >
                  Something else
                </Typography>
              </Box>
            </Box>

            {entry.freeOpen && (
              <TextField
                size="small"
                autoFocus
                placeholder="Type your answer..."
                value={entry.freeText}
                disabled={submitted}
                onChange={(e) => handleFreeText(qIndex, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isComplete(answers)) {
                    e.preventDefault();
                    submit(buildPayload(answers));
                  }
                }}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: radii.nested,
                    fontSize: theme.typography.pxToRem(12),
                    backgroundColor: theme.palette.background.paper
                  }
                }}
              />
            )}
          </Box>
        );
      })}

      {(!singleTapMode || answers.some((entry) => entry.freeOpen)) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            disableElevation
            disabled={submitted || !isComplete(answers)}
            onClick={() => submit(buildPayload(answers))}
            sx={{
              textTransform: 'none',
              fontFamily: theme.typography.fontFamily,
              fontWeight: 600,
              fontSize: theme.typography.pxToRem(12.5),
              borderRadius: radii.pill,
              px: 2,
              py: 0.6
            }}
          >
            {submitted ? 'Sent' : 'Send answers'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default UserQuestionCard;
